"use client";

import type { ReactNode } from "react";
import * as stylex from "@stylexjs/stylex";
import { Button, ButtonLink } from "@softmax/design-system";
import type { OnboardingProgress } from "./onboarding-progress";
import { useOnboardingProgress } from "./use-onboarding-progress";
import { StepRow } from "./step/onboarding-step";
import { CompletedStep } from "./step/onboarding-completed-step";
import { OnboardingActiveStep } from "./step/onboarding-active-step";
import { styles } from "./onboarding.styles";

type OnboardingWizardProps = Readonly<{
  progress: OnboardingProgress;
  agentLabel: string;
  leagueName: string;
  signInHref: string;
  doorHref: string;
  doorLabel: string;
  continueCommand: string;
  authFailed?: boolean;
  pollBroken?: boolean;
  playerForm: ReactNode;
  agentHandOff: ReactNode;
  onSignIn: () => void;
  onSwitchAccount: () => void;
  onRetry: () => void;
  onCopy: (command: string) => Promise<void>;
}>;

export function OnboardingWizard({
  progress,
  agentLabel,
  leagueName,
  signInHref,
  doorHref,
  doorLabel,
  continueCommand,
  authFailed = false,
  pollBroken = false,
  playerForm,
  agentHandOff,
  onSignIn,
  onSwitchAccount,
  onRetry,
  onCopy,
}: OnboardingWizardProps) {
  const {
    retryVisible,
    finished,
    activeIndex,
    visibleSteps,
    liveRowRef,
    recentlyChanged,
    announcement,
  } = useOnboardingProgress({ progress, pollBroken });
  return (
    <>
      <div aria-live="polite" {...stylex.props(styles.hidden)}>
        {announcement}
      </div>
      {/* Keep retry mounted when new progress replaces the active step. */}
      {retryVisible ? (
        <div {...stylex.props(styles.connectionStatus)}>
          <output {...stylex.props(styles.copy, pollBroken && styles.error)}>
            {pollBroken
              ? "Can’t reach Softmax right now — still trying."
              : "Connection restored."}
          </output>
          <Button variant="quiet" size="sm" onClick={onRetry}>
            Check again
          </Button>
        </div>
      ) : null}
      <ol data-wizard-room {...stylex.props(styles.list)}>
        {visibleSteps.map((step, index) => {
          const active =
            activeIndex !== -1 && index === visibleSteps.length - 1;
          return (
            <StepRow
              key={step.key}
              n={step.n}
              done={!active}
              animateIn={recentlyChanged.has(step.key)}
              rowRef={index === visibleSteps.length - 1 ? liveRowRef : null}
            >
              {active ? (
                <OnboardingActiveStep
                  step={step}
                  progress={progress}
                  pollBroken={pollBroken}
                  agentLabel={agentLabel}
                  authFailed={authFailed}
                  signInHref={signInHref}
                  onSignIn={onSignIn}
                  playerForm={playerForm}
                  agentHandOff={agentHandOff}
                  continueCommand={continueCommand}
                  onCopy={onCopy}
                  leagueName={leagueName}
                />
              ) : (
                <CompletedStep
                  step={step}
                  progress={progress}
                  agentLabel={agentLabel}
                  onSwitchAccount={onSwitchAccount}
                />
              )}
            </StepRow>
          );
        })}
        {finished ? (
          <li data-finale {...stylex.props(styles.row, styles.finale)}>
            <span
              aria-hidden="true"
              data-finale-badge
              {...stylex.props(styles.badge, styles.finaleBadge)}
            />
            <div {...stylex.props(styles.rowBody)}>
              <p {...stylex.props(styles.title)}>You’re in.</p>
              <div {...stylex.props(styles.action)}>
                <ButtonLink variant="primary" href={doorHref}>
                  {doorLabel}
                </ButtonLink>
              </div>
            </div>
          </li>
        ) : null}
      </ol>
    </>
  );
}
